/* @flow */

import React, { useState } from 'react';

import Button from 'rsuite/Button';
import Form from 'rsuite/Form';
import ButtonToolbar from 'rsuite/ButtonToolbar';
import Message from 'rsuite/Message';
import Nav from 'rsuite/Nav';
import DatePicker from 'rsuite/DatePicker';
import SelectPicker from 'rsuite/SelectPicker';

import VisibleIcon from '@rsuite/icons/Visible';
import UnvisibleIcon from '@rsuite/icons/Unvisible';

import classNames from 'classnames';
import { createEmailPass, signInEmailPass } from '../actions/account';
import { useNavigate } from 'react-router-dom';

import './Enter.css';

import zonesList from '../constants/zones.json';

const departmentZones = {
  'LA PAZ': zonesList,
  'EL ALTO': zonesList,
  'SANTA CRUZ': [
    'CENTRO',
    'VILLA 1° DE MAYO',
    'PAILÓN',
    'VILLA TERCERA',
    'EQUIPETROL',
  ],
  COCHABAMBA: ['CERCADO', 'SOUTH', 'NORTE', 'ALASITAS', 'VILLA SANTA ROSA'],
  POTOSI: ['COSTA', 'MESTIZO', 'CENTRO', 'HUANUNI', 'VILLA AURORA'],
  TARIJA: ['CIRCUNVALACION', 'BOLIVAR', 'SAN LUIS', 'LA LOMA', 'VILLA TUPA'],
  CHUQUISACA: ['SUCRE', 'ALTIPLANO', 'SAN FRANCISCO', 'CERCADO', 'POTOSI'],
  ORURO: ['NORTE', 'SUR', 'CERCADO', 'VILLA MONTES', 'COTAGRO'],
  BENI: ['BENI', 'PALMAREJA', 'RIBERALTA', 'TRINIDAD', 'GUAYARAMERIN'],
  PANDO: ['MUNDO', 'MUNICIPAL', 'PUERTO HASTA', 'SAN JAVIER', 'BOLIVIA'],
};

function Login(props: Object): Object {
  let navigate = useNavigate();
  const [loginActive, setLoginActive] = useState(true);
  const [formValue, setFormValue] = useState({
    email: '',
    password: '',
    fullName: '',
    birthdate: null,
    phone: '',
    addressZone: '',
    addressStreet: '',
    addressNumber: '',
    city: '',
    zone: '',
  });
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackType, setFeedbackType] = useState('error');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [zones, setZones] = useState([]);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);

  function setFeedback(type, message) {
    setFeedbackType(type);
    setFeedbackMessage(message);
  }

  function clearFeedback() {
    setFeedbackMessage('');
    setFeedbackType('error');
  }

  function onClickGoToSignIn() {
    clearFeedback();
    setLoginActive(true);
  }
  function onClickGoToCreate() {
    clearFeedback();
    setLoginActive(false);
  }

  function onCityChange(city) {
    setFormValue((prev) => ({ ...prev, city, zone: '' }));
    if (city && departmentZones[city.toUpperCase()]) {
      setZones(departmentZones[city.toUpperCase()]);
    } else {
      setZones([]);
    }
  }
  function onCreateAccount() {
    if (isCreatingAccount) {
      return;
    }

    clearFeedback();
    // Validar que todos los campos estén llenos y correctos
    // Validaciones y formateos personalizados
    let fullName = (formValue.fullName || '').trim().replace(/\s+/g, ' ');
    let email = (formValue.email || '').trim();
    const password = (formValue.password || '').trim();
    let phone = (formValue.phone || '').trim();
    let addressZone = (formValue.addressZone || '').trim();
    let addressStreet = (formValue.addressStreet || '').trim();
    let addressNumber = (formValue.addressNumber || '').trim();
    const city = (formValue.city || '').trim();
    let zone = (formValue.zone || '').trim();
    const birthdate = formValue.birthdate;

    // Nombre: debe tener al menos 3 palabras (nombre y dos apellidos), todo en mayúscula
    const nameParts = fullName.split(' ');
    if (nameParts.length < 3) {
      setFeedback('error', 'Debe ingresar nombre y dos apellidos.');
      return;
    }
    fullName = fullName.toUpperCase();

    // Email: validación básica
    const emailRegex = /^[\w-.]+@([\w-]+\.)+[a-zA-Z]{2,}$/;
    if (!email) {
      setFeedback('error', 'Email es obligatorio');
      return;
    }
    if (!emailRegex.test(email)) {
      setFeedback('error', 'El email no es válido');
      return;
    }

    // Teléfono: solo números, 8 dígitos (ajusta si tu país usa otro formato)
    const phoneDigits = phone.replace(/\D/g, '');
    if (!phoneDigits || phoneDigits.length !== 8) {
      setFeedback('error', 'El teléfono debe tener exactamente 8 dígitos');
      return;
    }
    phone = phoneDigits;

    // Validar cada parte de la dirección
    if (!addressZone) {
      setFeedback('error', 'Zona de la dirección es obligatoria');
      return;
    }
    if (!addressStreet) {
      setFeedback('error', 'Calle o avenida de la dirección es obligatoria');
      return;
    }
    if (!addressNumber || !/^\d+$/.test(addressNumber)) {
      setFeedback(
        'error',
        'Número de puerta de la dirección es obligatorio y debe ser numérico',
      );
      return;
    }
    let address =
      `${addressZone}, ${addressStreet}, #${addressNumber}`.toUpperCase();
    zone = zone.toUpperCase();

    if (!city) {
      setFeedback('error', 'Ciudad es obligatoria');
      return;
    }
    if (!zone) {
      setFeedback('error', 'Zona es obligatoria');
      return;
    }
    const isValidBirthdate =
      birthdate instanceof Date && !isNaN(birthdate) && birthdate <= new Date();
    if (!isValidBirthdate) {
      setFeedback('error', 'Fecha de nacimiento inválida');
      return;
    }
    if (!password) {
      setFeedback('error', 'Password es obligatorio');
      return;
    }

    const userData = {
      email,
      password,
      fullName: fullName,
      name: fullName,
      birthdate,
      phone,
      address,
      city,
      zone,
    };

    setIsCreatingAccount(true);
    createEmailPass(userData)
      .then((res) => {
        setLoginActive(true);
        setFormValue((prev) => ({
          ...prev,
          password: '',
        }));

        if (res?.verificationEmailSent) {
          setFeedback(
            'info',
            `Te enviamos un correo de verificación a ${email}. Verifica tu email y luego inicia sesión.`,
          );
          return;
        }

        setFeedback(
          'warning',
          'La cuenta fue creada, pero no pudimos enviar el correo de verificación. Intenta iniciar sesión para reenviarlo.',
        );
      })
      .catch((error) => {
        let errorMsg = 'Error';
        if (error.code === 'auth/email-already-in-use') {
          errorMsg = 'The email address is already in use.';
        } else if (error.code === 'auth/invalid-email') {
          errorMsg = 'The email address is not valid.';
        } else if (error.code === 'auth/operation-not-allowed') {
          errorMsg = 'Operation not allowed.';
        } else if (error.code === 'auth/weak-password') {
          errorMsg = 'The password is too weak.';
        }
        setFeedback('error', window.lc(errorMsg));
      })
      .finally(() => {
        setIsCreatingAccount(false);
      });
  }
  function onLoginAccount() {
    if (isSigningIn) {
      return;
    }

    clearFeedback();
    if (formValue.email && formValue.password) {
      setIsSigningIn(true);
      signInEmailPass(formValue.email, formValue.password)
        .then((props: Object) => {
          if (props.user) {
            navigate('/');
          }
        })
        .catch((error) => {
          console.error('error', error);
          setFeedback(
            error.code === 'auth/email-not-verified' ? 'warning' : 'error',
            error.message,
          );
        })
        .finally(() => {
          setIsSigningIn(false);
        });
    }
  }

  return (
    <div
      className={classNames('mb-enter', {
        login: loginActive,
        logon: !loginActive,
      })}
    >
      <div className="mb-mobile-tab">
        <Nav appearance="tabs" justified>
          <Nav.Item active={loginActive} onSelect={onClickGoToSignIn}>
            {window.lc('Sign In')}
          </Nav.Item>
          <Nav.Item active={!loginActive} onSelect={onClickGoToCreate}>
            {window.lc('Sign On')}
          </Nav.Item>
        </Nav>
      </div>
      <div className="mb-signin">
        <div className="inactive">
          <h4>{window.lc('Welcome Back!')}</h4>
          <Button onClick={onClickGoToSignIn}>{window.lc('Sign In')}</Button>
        </div>
        <div className="active">
          <h4>{window.lc('Sign In to {PageName}!')}</h4>
          <Form
            onChange={(formValue) => {
              setFormValue((prev) => ({ ...prev, ...formValue }));
            }}
            style={{ marginTop: 10 }}
          >
            <Form.Group>
              <label>Email</label>
              <Form.Control name="email" type="email" />
              <Form.HelpText tooltip>{window.lc('Required')}</Form.HelpText>
            </Form.Group>
            <Form.Group>
              <label>Password</label>
              <Form.Control
                name="password"
                type={passwordVisible ? 'text' : 'password'}
              />
              <a
                className="cl-visibility"
                onClick={() => {
                  setPasswordVisible(!passwordVisible);
                }}
              >
                {passwordVisible ? <VisibleIcon /> : <UnvisibleIcon />}
              </a>
              <Form.HelpText tooltip>{window.lc('Required')}</Form.HelpText>
            </Form.Group>
            {feedbackMessage ? (
              <Message type={feedbackType}>{feedbackMessage}</Message>
            ) : null}
            <Form.Group className="button-center">
              <ButtonToolbar>
                <Button
                  appearance="primary"
                  onClick={onLoginAccount}
                  loading={isSigningIn}
                  disabled={isSigningIn}
                >
                  {window.lc('Enter')}
                </Button>
              </ButtonToolbar>
            </Form.Group>
          </Form>
        </div>
      </div>
      <div className="mb-signup">
        <div className="active">
          <h4>{window.lc('Create Account')}</h4>
          <div className="create-desc">
            <div>Crea tu cuenta y comienza a comprar!</div>
            <div>Registrarte es simple y tarda solo minutos!</div>
          </div>
          <Form
            onChange={(formValue) => {
              setFormValue((prev) => ({ ...prev, ...formValue }));
            }}
            style={{ marginTop: 10 }}
          >
            <Form.Group>
              <label>Full Name *</label>
              <Form.Control name="fullName" type="text" />
              <Form.HelpText tooltip>{window.lc('Required')}</Form.HelpText>
            </Form.Group>
            <Form.Group>
              <label>Email *</label>
              <Form.Control name="email" type="email" />
              <Form.HelpText tooltip>{window.lc('Required')}</Form.HelpText>
            </Form.Group>
            <Form.Group>
              <label>Password *</label>
              <Form.Control
                name="password"
                type={passwordVisible ? 'text' : 'password'}
              />
              <a
                className="cl-visibility"
                onClick={() => {
                  setPasswordVisible(!passwordVisible);
                }}
              >
                {passwordVisible ? <VisibleIcon /> : <UnvisibleIcon />}
              </a>
              <Form.HelpText tooltip>{window.lc('Required')}</Form.HelpText>
            </Form.Group>
            <Form.Group>
              <label>Birthdate *</label>
              <DatePicker
                placement="topStart"
                value={formValue.birthdate}
                onChange={(value) =>
                  setFormValue((prev) => ({ ...prev, birthdate: value }))
                }
                format="yyyy-MM-dd"
                disabledDate={(date) => date > new Date()}
                placeholder="Select birthdate"
              />
              <Form.HelpText tooltip>
                {window.lc('No se puede cambiar a futuro')}
              </Form.HelpText>
            </Form.Group>
            <Form.Group>
              <label>Phone *</label>
              <Form.Control name="phone" type="tel" />
              <Form.HelpText tooltip>{window.lc('Required')}</Form.HelpText>
            </Form.Group>
            <Form.Group>
              <label>Zona *</label>
              <Form.Control
                name="addressZone"
                type="text"
                value={formValue.addressZone || ''}
                onChange={(value) =>
                  setFormValue((prev) => ({ ...prev, addressZone: value }))
                }
              />
              <Form.HelpText tooltip>{window.lc('Required')}</Form.HelpText>
            </Form.Group>
            <Form.Group>
              <label>Calle o Avenida *</label>
              <Form.Control
                name="addressStreet"
                type="text"
                value={formValue.addressStreet || ''}
                onChange={(value) =>
                  setFormValue((prev) => ({ ...prev, addressStreet: value }))
                }
              />
              <Form.HelpText tooltip>{window.lc('Required')}</Form.HelpText>
            </Form.Group>
            <Form.Group>
              <label>Número de Puerta *</label>
              <Form.Control
                name="addressNumber"
                type="text"
                value={formValue.addressNumber || ''}
                onChange={(value) =>
                  setFormValue((prev) => ({ ...prev, addressNumber: value }))
                }
              />
              <Form.HelpText tooltip>{window.lc('Required')}</Form.HelpText>
            </Form.Group>
            <Form.Group>
              <label>Ciudad *</label>
              <SelectPicker
                value={formValue.city}
                onChange={onCityChange}
                data={Object.keys(departmentZones).map((city) => ({
                  label: city,
                  value: city,
                }))}
                style={{ width: '100%' }}
                placeholder="Select city"
              />
              <Form.HelpText tooltip>{window.lc('Required')}</Form.HelpText>
            </Form.Group>
            <Form.Group>
              <label>Zona *</label>
              <SelectPicker
                value={formValue.zone}
                onChange={(value) =>
                  setFormValue((prev) => ({ ...prev, zone: value }))
                }
                data={zones.map((zone) => ({ label: zone, value: zone }))}
                style={{ width: '100%' }}
                placeholder="Selecciona la zona mas cercana a tu dirección"
                disabled={!formValue.city}
              />
              <Form.HelpText tooltip>{window.lc('Required')}</Form.HelpText>
            </Form.Group>
            {feedbackMessage ? (
              <Message type={feedbackType}>{feedbackMessage}</Message>
            ) : null}
            <Form.Group className="button-center">
              <ButtonToolbar>
                <Button
                  appearance="primary"
                  onClick={onCreateAccount}
                  loading={isCreatingAccount}
                  disabled={isCreatingAccount}
                >
                  {window.lc('Create')}
                </Button>
              </ButtonToolbar>
            </Form.Group>
          </Form>
        </div>
        <div className="inactive">
          <h4>{window.lc('Hello, Friend!')}</h4>
          <Button onClick={onClickGoToCreate}>
            {window.lc('Create Account')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default Login;
